import { useEffect, useState, type ReactNode } from "react";
import { ApiError, createApiClient } from "../../api/client";
import type {
  BillPayload,
  DeviceStatus,
  GeneralSettings,
  KitchenPayload,
  PrintJob,
  ReceiptPayload,
  ReportPayload,
  SettlementPayload,
  TestPayload,
} from "../../api/types";
import { tillCredentials } from "../../app/session";
import { t } from "../../i18n";
import { formatDate, formatTime } from "../../lib/time";
import { QrCode } from "../../ui/QrCode";
import { CourierBon } from "../couriers/CourierBon";
import { Receipt } from "../orders/Receipt";
import { ReportReceipt } from "../reports/ReportReceipt";
import { xPrint, zPrint } from "../reports/reports";
import { Bill } from "../../tafo-app/waiter/Bill";
import { KitchenTicket } from "./KitchenTicket";

/**
 * How much sharper the page is drawn (`?scale=2`): the Tafo app for Windows draws at 2, two dots per
 * CSS pixel at 203 dpi; a browser shows it at 1.
 */
export function printScale(search: string): 1 | 2 | 3 {
  const scale = Number(new URLSearchParams(search).get("scale") ?? "1");
  return scale === 2 || scale === 3 ? scale : 1;
}

/**
 * The printable width the Tafo app for Windows draws at (`?dots=512`, Furkan, 09/10/2026: a Bixolon
 * printed too large), in dots; null for the width of the paper (576 or 384 dots).
 */
export function printDots(search: string, scale: number): number | null {
  const dots = Number(new URLSearchParams(search).get("dots") ?? "");
  return Number.isInteger(dots) && dots >= 256 && dots <= 832 && dots % scale === 0 ? dots : null;
}

/** "/print/<job id>": the page the Tafo app for Windows renders to print a job. */
export function printJobId(pathname: string): string | null {
  const match = /^\/print\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/.exec(pathname);
  return match?.[1] ?? null;
}

/** Tells the Tafo app the page is drawn (or why not): it waits for this before it takes the picture. */
function done(state: "1" | "error", error?: string) {
  document.documentElement.dataset.printReady = state;
  if (error !== undefined) document.documentElement.dataset.printError = error;
}

/** The shop as on every receipt, from Algemeen in the job (the onboarding details are not needed). */
const shopOf = (general: GeneralSettings): DeviceStatus["shop"] => ({
  name: general.receiptName,
  city: general.city,
  status: "active",
});

/**
 * The print page (blok I, ADR 0010): the Tafo app for Windows opens it out of sight for each job, at
 * the width of the paper (72 mm = 288 CSS pixels, drawn twice as sharp: 576 dots at 203 dpi; 48 mm for
 * 58 mm paper), takes a picture and sends it to the printer. It draws the job with the same components as the till
 * (the receipt, X and Z, afrekenbon), so the paper looks like the screen. Each job holds everything as
 * it was when it was queued.
 */
export function PrintPage({ jobId }: { jobId: string }) {
  const [job, setJob] = useState<PrintJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.classList.add("printpage");
    document.title = t.print.page.loading;
    const api = createApiClient(tillCredentials);
    let stopped = false;
    api
      .request<{ job: PrintJob }>("GET", `/device/print-jobs/${jobId}`, { retry: true })
      .then((answer) => {
        if (stopped) return;
        document.title = answer.job.title;
        setJob(answer.job);
      })
      .catch((failure: unknown) => {
        if (stopped) return;
        const message = failure instanceof ApiError ? failure.message : t.print.page.missing;
        setError(message);
        done("error", message);
      });
    return () => {
      stopped = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (job === null) return;
    let stopped = false;
    // ready once the fonts and the images (the Tafo logo) are there
    void Promise.all([
      document.fonts.ready,
      ...[...document.images].map((image) => image.decode().catch(() => undefined)),
    ]).then(() => {
      if (!stopped) done("1");
    });
    return () => {
      stopped = true;
    };
  }, [job]);

  if (error !== null) return <p className="printmsg">{error}</p>;
  if (job === null) return <p className="printmsg">{t.print.page.loading}</p>;
  const paper = job.printer.paper;
  const scale = printScale(window.location.search);
  const dots = printDots(window.location.search, scale);
  return (
    <div
      className={`printroot p${String(paper)} z${String(scale)}`}
      // the printer's own width (a Bixolon: 512 dots), when the Tafo app says it
      style={dots === null ? undefined : { width: `${String(dots / scale)}px` }}
    >
      {content(job)}
    </div>
  );
}

/**
 * Above a copy of the receipt in the kitchen: nothing, the copy is the receipt itself (Furkan, 07/10/2026:
 * "bovenaan kopie afrekening mag je verwijderen, dat moet niet op de bon staan"). Only a copy of a changed
 * order says what changed, because the kitchen has to act on it.
 */
export function CopyChanges({ changes }: { changes: { added: string[]; removed: string[] } | null }) {
  if (changes === null) return null;
  return (
    <div className="copybanner">
      <b>
        {t.print.copyChanges(
          [
            changes.added.length > 0 ? t.print.plus(changes.added.join(", ")) : null,
            changes.removed.length > 0 ? t.print.minus(changes.removed.join(", ")) : null,
          ]
            .filter((part): part is string => part !== null)
            .join(" · "),
        )}
      </b>
    </div>
  );
}

/** What the job prints, after its kind. */
function content(job: PrintJob): ReactNode {
  const paper = job.printer.paper;
  switch (job.kind) {
    case "kitchen":
      return <KitchenTicket ticket={job.payload as KitchenPayload} paper={paper} />;
    case "receipt": {
      const { order, general, kitchenCopy } = job.payload as ReceiptPayload;
      const changes = kitchenCopy?.changes ?? null;
      return (
        <>
          <CopyChanges changes={changes} />
          <Receipt order={order} shop={shopOf(general)} general={general} paper={paper} />
        </>
      );
    }
    case "report": {
      const payload = job.payload as ReportPayload;
      return (
        <ReportReceipt
          print={
            "z" in payload
              ? zPrint(payload.z)
              : xPrint(payload.x, shopOf(payload.general), payload.general, new Date(job.createdAt))
          }
        />
      );
    }
    case "settlement": {
      const { statement, shop, at } = job.payload as SettlementPayload;
      return <CourierBon shop={shop} statement={statement} at={at} />;
    }
    case "bill": {
      // "🧾 Rekening" from the Tafo app (blok J): not a receipt
      const { order, general, waiter, at, parts } = job.payload as BillPayload;
      return (
        <Bill
          order={order}
          shop={shopOf(general)}
          general={general}
          waiter={waiter}
          at={at}
          paper={paper}
          parts={parts ?? []}
        />
      );
    }
    case "test":
      return <TestTicket test={job.payload as TestPayload} at={job.createdAt} />;
    case "drawer":
      // opening the drawer prints nothing
      return null;
  }
}

/**
 * "Test" in Beheer › Printers & toestellen: the printer's name and paper, a ruler over the full width,
 * a QR code (graphics), the time; when all of it is on the paper, the printer is set up right.
 */
function TestTicket({ test, at }: { test: TestPayload; at: string }) {
  const text = t.print.test;
  return (
    <article className={`bon testbon${test.paper === 58 ? " w58" : ""}`} aria-label={text.title}>
      <div className="ktitle inv">{text.title}</div>
      <div className="c blk">
        <b>{text.printer(test.printer, test.paper)}</b>
        <div>
          {formatDate(at)} {formatTime(at)}
        </div>
      </div>
      <div className="ruler" aria-hidden="true">
        {text.ruler}
      </div>
      <p className="blk">{text.ok}</p>
      <p className="blk">{text.narrower}</p>
      <div className="bqr testqr">
        <QrCode text="https://tafo.be" label={text.qr} />
      </div>
      <p className="c">{text.qr}</p>
      <div className="bontafo">
        <img src="/tafo-logo.png" alt="Tafo" />
        <span>{t.receipt.tafo}</span>
      </div>
    </article>
  );
}
